import React, { useState, useEffect, useContext } from 'react';
import { AuthContext, API } from '../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { MessageCircle, ThumbsUp, Heart, Plus, Send } from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

const Community = () => {
  const { user } = useContext(AuthContext);
  const [posts, setPosts] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createPostOpen, setCreatePostOpen] = useState(false);
  const [category, setCategory] = useState('');
  const [newComment, setNewComment] = useState('');
  const [newPost, setNewPost] = useState({
    title: '',
    content: '',
    category: 'support',
    post_type: 'question',
    is_anonymous: false
  });

  const categories = [
    { value: '', label: 'All Categories' },
    { value: 'behavior', label: 'Behavior' },
    { value: 'communication', label: 'Communication' },
    { value: 'school', label: 'School' },
    { value: 'wins', label: 'Wins' },
    { value: 'support', label: 'Support' }
  ];

  const postTypes = [
    { value: 'question', label: 'Question' },
    { value: 'win', label: 'Win' },
    { value: 'advice', label: 'Advice' },
    { value: 'story', label: 'Story' }
  ];

  useEffect(() => {
    loadPosts();
  }, [category]);

  useEffect(() => {
    if (selectedPost) {
      loadComments();
    }
  }, [selectedPost]);

  const loadPosts = async () => {
    setLoading(true);
    try {
      const params = category ? `?category=${category}` : '';
      const response = await API.get(`/community/posts${params}`);
      setPosts(response.data);
    } catch (error) {
      console.error('Failed to load posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadComments = async () => {
    try {
      const response = await API.get(`/community/posts/${selectedPost.post_id}/comments`);
      setComments(response.data);
    } catch (error) {
      console.error('Failed to load comments:', error);
    }
  };

  const handleCreatePost = async (e) => {
    e.preventDefault();
    try {
      await API.post('/community/posts', newPost);
      toast.success('Post created!');
      setCreatePostOpen(false);
      setNewPost({
        title: '',
        content: '',
        category: 'support',
        post_type: 'question',
        is_anonymous: false
      });
      loadPosts();
    } catch (error) {
      toast.error('Failed to create post');
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    try {
      await API.post(`/community/posts/${selectedPost.post_id}/comments`, {
        content: newComment
      });
      setNewComment('');
      loadComments();
      toast.success('Comment added!');
    } catch (error) {
      toast.error('Failed to add comment');
    }
  };

  const handleReaction = async (postId, reaction) => {
    try {
      await API.post(`/community/posts/${postId}/react?reaction=${reaction}`);
      loadPosts();
    } catch (error) {
      console.error('Failed to react:', error);
    }
  };

  const getCategoryColor = (cat) => {
    const colors = {
      behavior: 'bg-blue-100 text-blue-700',
      communication: 'bg-green-100 text-green-700',
      school: 'bg-purple-100 text-purple-700',
      wins: 'bg-yellow-100 text-yellow-700',
      support: 'bg-pink-100 text-pink-700'
    };
    return colors[cat] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="space-y-8 max-w-6xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold mb-2">Community</h1>
          <p className="text-muted-foreground">Connect, share, and support each other</p>
        </div>
        <Dialog open={createPostOpen} onOpenChange={setCreatePostOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="w-4 h-4 mr-2" />
              New Post
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Create New Post</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreatePost} className="space-y-4">
              <div>
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  value={newPost.title}
                  onChange={(e) => setNewPost({ ...newPost, title: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="content">Content</Label>
                <Textarea
                  id="content"
                  rows={6}
                  value={newPost.content}
                  onChange={(e) => setNewPost({ ...newPost, content: e.target.value })}
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Category</Label>
                  <Select
                    value={newPost.category}
                    onValueChange={(value) => setNewPost({ ...newPost, category: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.slice(1).map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>
                          {cat.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Post Type</Label>
                  <Select
                    value={newPost.post_type}
                    onValueChange={(value) => setNewPost({ ...newPost, post_type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {postTypes.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={newPost.is_anonymous}
                  onCheckedChange={(checked) => setNewPost({ ...newPost, is_anonymous: checked })}
                />
                <Label>Post anonymously</Label>
              </div>
              <Button type="submit" className="w-full">Create Post</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Category Filter */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {categories.map(cat => (
          <Button
            key={cat.value}
            variant={category === cat.value ? 'default' : 'outline'}
            onClick={() => setCategory(cat.value)}
          >
            {cat.label}
          </Button>
        ))}
      </div>

      {/* Posts List */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {loading ? (
          <div className="col-span-2 text-center py-12">
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
          </div>
        ) : posts.length > 0 ? (
          posts.map(post => (
            <Card
              key={post.post_id}
              className="cursor-pointer hover:shadow-lg transition-shadow"
              onClick={() => setSelectedPost(post)}
            >
              <CardHeader>
                <div className="flex items-start justify-between mb-2">
                  <Badge className={getCategoryColor(post.category)}>
                    {post.category}
                  </Badge>
                  <span className="text-xs text-muted-foreground">
                    {formatDistanceToNow(new Date(post.created_at), { addSuffix: true })}
                  </span>
                </div>
                <CardTitle>{post.title}</CardTitle>
                <CardDescription>
                  by {post.author_name} • {post.post_type}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm line-clamp-3 mb-4">{post.content}</p>
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleReaction(post.post_id, 'helpful');
                    }}
                    className="flex items-center gap-1 hover:text-blue-600"
                  >
                    <ThumbsUp className="w-4 h-4" />
                    {post.reactions?.helpful || 0}
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleReaction(post.post_id, 'support');
                    }}
                    className="flex items-center gap-1 hover:text-pink-600"
                  >
                    <Heart className="w-4 h-4" />
                    {post.reactions?.support || 0}
                  </button>
                  <span className="flex items-center gap-1">
                    <MessageCircle className="w-4 h-4" />
                    {post.comment_count || 0}
                  </span>
                </div>
              </CardContent>
            </Card>
          ))
        ) : (
          <div className="col-span-2 text-center py-12">
            <p className="text-muted-foreground">No posts yet. Be the first to share!</p>
          </div>
        )}
      </div>

      {/* Post Detail Dialog */}
      {selectedPost && (
        <Dialog open={!!selectedPost} onOpenChange={() => setSelectedPost(null)}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <div className="flex items-start justify-between mb-2">
                <Badge className={getCategoryColor(selectedPost.category)}>
                  {selectedPost.category}
                </Badge>
                <span className="text-xs text-muted-foreground">
                  {formatDistanceToNow(new Date(selectedPost.created_at), { addSuffix: true })}
                </span>
              </div>
              <DialogTitle className="text-2xl">{selectedPost.title}</DialogTitle>
              <p className="text-sm text-muted-foreground">
                by {selectedPost.author_name} • {selectedPost.post_type}
              </p>
            </DialogHeader>
            <div className="space-y-6">
              <p className="whitespace-pre-wrap">{selectedPost.content}</p>
              
              <div className="flex items-center gap-4 pt-4 border-t">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleReaction(selectedPost.post_id, 'helpful')}
                >
                  <ThumbsUp className="w-4 h-4 mr-2" />
                  Helpful ({selectedPost.reactions?.helpful || 0})
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleReaction(selectedPost.post_id, 'support')}
                >
                  <Heart className="w-4 h-4 mr-2" />
                  Support ({selectedPost.reactions?.support || 0})
                </Button>
              </div>

              {/* Comments */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lg">Comments ({comments.length})</h3>
                {comments.map(comment => (
                  <Card key={comment.comment_id} className="bg-gray-50">
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{comment.author_name}</span>
                        <span className="text-xs text-muted-foreground">
                          {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
                        </span>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm">{comment.content}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Add Comment */}
              <form onSubmit={handleAddComment} className="flex gap-2">
                <Input
                  placeholder="Add a comment..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                />
                <Button type="submit" size="icon">
                  <Send className="w-4 h-4" />
                </Button>
              </form>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default Community;